import { isHeartbeatRow } from './affHeartbeatPopup';

/**
 * Text shown beside an AFF aircraft icon (#781).
 *
 * The map draws rotated silhouettes with no identifying text, so one airframe
 * cannot be told from another without clicking. The registration rides beside
 * the icon permanently.
 *
 * The heartbeat is a service-health row rather than an aircraft and carries no
 * registration to show — the same exclusion it already gets from the aircraft
 * lists and exports.
 */
export const registrationLabel = (props: Record<string, unknown>): string | null => {
  const name = typeof props?.name === 'string' ? props.name : null;

  if (isHeartbeatRow({ name })) return null;
  if (name === null || name.trim() === '') return null;

  return name;
};

/**
 * Text shown when the pointer rests on an aircraft icon (#781).
 *
 * `label` arrives from Selkirk already composed — call sign and staleness
 * phrase together, e.g. `T649 - 1091 minutes ago` — and is shown as it
 * arrives. The hover this replaces split that string on its separator, kept
 * the head and recomputed its own relative time, discarding the half the
 * request asks to see.
 */
/** Class the collision pass uses to find labels again after they are drawn. */
export const AFF_LABEL_CLASS = 'aff-marker-label';

/**
 * Build the element that carries a registration beside its icon (#781).
 *
 * Positioned clear of the icon's own box so it never covers the airframe, and
 * kept out of the icon's rotation transform — a label turning with the
 * aircraft's heading would be unreadable on most headings and upside down on
 * some.
 *
 * Marked aria-hidden: the marker already announces the aircraft, and a second
 * reading of the registration is noise rather than information.
 */
export const createLabelElement = (registration: string | null): HTMLElement | null => {
  if (registration === null) return null;

  const el = document.createElement('span');
  el.className = AFF_LABEL_CLASS;
  el.textContent = registration;
  el.setAttribute('aria-hidden', 'true');

  return el;
};

/**
 * Zoom at or above which aircraft registrations are drawn (#781).
 *
 * Matches VAR_ZOOM.label — the zoom this map has already decided feature
 * labels become legible at — rather than a number picked for AFF alone. The
 * map's own floor is 4.
 *
 * Below this, the whole fleet occupies a few hundred pixels and nearly every
 * label would lose the collision pass regardless; suppressing by zoom reaches
 * the same screen without measuring anything.
 */
export const AFF_LABEL_ZOOM = 6;

export const labelsVisibleAtZoom = (zoom: number): boolean => zoom >= AFF_LABEL_ZOOM;

/** A label's footprint on screen, in CSS pixels. */
export interface LabelRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface LabelBox {
  esn: string;
  status: 'flying' | 'landed';
  rect: LabelRect;
}

const overlaps = (a: LabelRect, b: LabelRect): boolean =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/**
 * Decide which aircraft labels survive when they collide (#781).
 *
 * A Mapbox symbol layer would hide colliding labels for us, but these are DOM
 * markers — nothing arbitrates, so every label draws over whatever sits
 * beneath it. We arbitrate in screen space instead.
 *
 * Greedy, best-first: walk the labels in priority order and keep one only if
 * it clears everything already kept. Flying outranks landed, because a moving
 * airframe is what a dispatcher is following; ties break on ESN so the same
 * label wins on every frame. Order-dependent tie-breaking would let labels
 * swap as the feature array reorders between refreshes, which reads on screen
 * as a rendering fault rather than a decision.
 *
 * A dropped label frees its space: it is not drawn, so it cannot obscure
 * anything, and treating it as occupied would let one crowded corner blank a
 * whole neighbourhood of labels that never collided with anything visible.
 */
export const resolveLabelCollisions = (boxes: LabelBox[]): string[] => {
  const byPriority = [...boxes].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'flying' ? -1 : 1;
    return a.esn < b.esn ? -1 : a.esn > b.esn ? 1 : 0;
  });

  const keptRects: LabelRect[] = [];
  const kept: string[] = [];

  for (const boxToPlace of byPriority) {
    if (keptRects.some((placed) => overlaps(placed, boxToPlace.rect))) continue;
    keptRects.push(boxToPlace.rect);
    kept.push(boxToPlace.esn);
  }

  return kept;
};

export const hoverLabel = (props: Record<string, unknown>): string => {
  const label = props?.label;
  if (typeof label === 'string' && label.trim() !== '') return label;

  const name = props?.name;
  if (typeof name === 'string' && name.trim() !== '') return name;

  return 'Unknown';
};
